import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createAdditionalClubRequest,
  fetchMe,
  fetchMyClubs,
  loginRequest,
  logoutRequest,
  registerClubRequest,
  setAccessToken,
  switchClubRequest,
  type AuthUser,
  type UserClub,
} from '../api';
import { syncProfilePrefs } from '../utils/ColorPrefs';

type AuthContextValue = {
  user: AuthUser | null;
  clubs: UserClub[];
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Техник: ТО + неисправности, без полётов/броней и правок налёта */
  isTechnician: boolean;
  /** Гость (покупатель): только просмотр борта / налёта / ТО / неисправностей */
  isGuest: boolean;
  /** Проведение ТО и закрытие неисправностей */
  canMaintain: boolean;
  /** Добавление полётов и броней */
  canFlyOrBook: boolean;
  /** Личный кабинет (не для гостя) */
  canUseProfile: boolean;
  /** Добавление неисправностей */
  canReportIssues: boolean;
  /** Аккаунт поддержки: вне клубов, видит только обращения */
  isSupport: boolean;
  currentUserId: number;
  currentUserName: string;
  login: (login: string, password: string) => Promise<void>;
  registerClub: (data: {
    club_name: string;
    name: string;
    login: string;
    password: string;
    is_pilot?: boolean;
  }) => Promise<void>;
  createAdditionalClub: (
    club_name: string,
    is_pilot?: boolean
  ) => Promise<void>;
  switchClub: (club_id: number) => Promise<void>;
  refreshClubs: () => Promise<void>;
  logout: () => Promise<void>;
  patchUser: (patch: Partial<AuthUser>) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function applySession(data: AuthUser & { access_token?: string }) {
  if (data.access_token) {
    setAccessToken(data.access_token);
  }
  const profile: AuthUser = {
    id: data.id,
    name: data.name,
    login: data.login,
    role: data.role,
    color: data.color ?? null,
    date_format: data.date_format === 'long' ? 'long' : 'dot',
    hide_colors: Boolean(data.hide_colors),
    club_id: data.club_id ?? null,
    club_role: data.club_role ?? null,
    club_name: data.club_name ?? null,
    is_pilot: data.is_pilot !== false,
    is_support: Boolean(data.is_support),
  };
  syncProfilePrefs(profile);
  return profile;
}

/** Аккаунт поддержки не состоит в клубах: список клубов ему не нужен (API отдаст 403). */
const loadClubsFor = async (profile: AuthUser): Promise<UserClub[]> => {
  if (profile.is_support) return [];
  try {
    return await fetchMyClubs();
  } catch {
    return [];
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [clubs, setClubs] = useState<UserClub[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshClubs = useCallback(async () => {
    try {
      const list = await fetchMyClubs();
      setClubs(list);
    } catch {
      setClubs([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        const me = await fetchMe();
        if (!cancelled) {
          const profile = applySession(me);
          setUser(profile);
          const list = await loadClubsFor(profile);
          if (!cancelled) setClubs(list);
        }
      } catch {
        setAccessToken(null);
        syncProfilePrefs(null);
        if (!cancelled) {
          setUser(null);
          setClubs([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (loginName: string, password: string) => {
    const data = await loginRequest(loginName, password);
    const profile = applySession(data);
    setUser(profile);
    setClubs(await loadClubsFor(profile));
  }, []);

  const registerClub = useCallback(
    async (data: {
      club_name: string;
      name: string;
      login: string;
      password: string;
    }) => {
      const session = await registerClubRequest(data);
      setUser(applySession(session));
      try {
        setClubs(await fetchMyClubs());
      } catch {
        setClubs([]);
      }
    },
    []
  );

  const createAdditionalClub = useCallback(
    async (club_name: string, is_pilot: boolean = true) => {
      const session = await createAdditionalClubRequest(club_name, is_pilot);
      setUser(applySession(session));
      setClubs(await fetchMyClubs());
      window.dispatchEvent(new Event('aircraft-updated'));
    },
    []
  );

  const switchClub = useCallback(async (club_id: number) => {
    const session = await switchClubRequest(club_id);
    const profile = applySession(session);
    setUser(profile);
    try {
      setClubs(await fetchMyClubs());
    } catch {
      setClubs((prev) => {
        const marked = prev.map((club) => ({
          ...club,
          active: club.club_id === club_id,
        }));
        if (marked.some((club) => club.club_id === club_id)) return marked;
        return [
          ...marked,
          {
            club_id,
            club_name: profile.club_name || '',
            role: profile.club_role || '',
            active: true,
          },
        ];
      });
    }
    window.dispatchEvent(new Event('aircraft-updated'));
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } catch {
      // cookies могли уже истечь
    }
    setAccessToken(null);
    syncProfilePrefs(null);
    setUser(null);
    setClubs([]);
  }, []);

  const patchUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      syncProfilePrefs(next);
      return next;
    });
  }, []);

  const isAdmin =
    user?.club_role === 'owner' ||
    user?.club_role === 'admin' ||
    user?.club_role === 'moderator';
  const isTechnician = user?.club_role === 'technician';
  const isGuest = user?.club_role === 'guest';
  const canMaintain = isAdmin || isTechnician;
  const canFlyOrBook =
    user?.club_role === 'admin' ||
    user?.club_role === 'pilot' ||
    (user?.club_role === 'owner' && user?.is_pilot !== false);
  const canUseProfile = Boolean(user?.id) && !isGuest;
  const canReportIssues = canFlyOrBook || isTechnician || isAdmin;
  const isSupport = Boolean(user?.is_support);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      clubs,
      loading,
      isAuthenticated: Boolean(user?.id),
      isAdmin,
      isTechnician,
      isGuest,
      canMaintain,
      canFlyOrBook,
      canUseProfile,
      canReportIssues,
      isSupport,
      currentUserId: user?.id || 0,
      currentUserName: user?.name || '',
      login,
      registerClub,
      createAdditionalClub,
      switchClub,
      refreshClubs,
      logout,
      patchUser,
    }),
    [
      user,
      clubs,
      loading,
      isAdmin,
      isTechnician,
      isGuest,
      canMaintain,
      canFlyOrBook,
      canUseProfile,
      canReportIssues,
      isSupport,
      login,
      registerClub,
      createAdditionalClub,
      switchClub,
      refreshClubs,
      logout,
      patchUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
